import { useEffect, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { requestPlacement, getLatestClusters } from "../lib/api";
import { isPlaceable } from "../lib/responses";
import type { Response } from "../lib/types";

const MAX_CONCURRENT = 3;

/**
 * Drives live placement of new responses into existing clusters.
 *
 * - Subscribes to `responses` INSERT/UPDATE for the question.
 * - When a row is placeable (visible AND cluster_id IS NULL), enqueues a
 *   placement call.
 * - Guarantees only one placement call per response is in flight at a time.
 * - Caps total in-flight calls at MAX_CONCURRENT.
 * - Skips the entire system until at least one cluster_run exists for the
 *   question — placement against an empty cluster set is a no-op on the
 *   server anyway, but skipping client-side avoids needless requests.
 *
 * Mounted on FacilitatorView (active question) and StageView. Both can run
 * concurrently because the edge function is idempotent.
 */
export function useLivePlacement(questionId: string | null) {
  const inFlightRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<string[]>([]);
  const hasClustersRef = useRef<boolean>(false);
  // Track which responses we've already attempted to place during this
  // session for the current question. Without this, a placement that comes
  // back as `uncategorized` writes the response row (updating
  // placement_confidence / placement_run_id without touching cluster_id),
  // the realtime UPDATE re-fires, consider() sees cluster_id IS NULL and
  // is_placeable, re-queues, edge function returns uncategorized again,
  // forever. We mark each id "attempted" after one placement round and
  // only re-attempt if SOMETHING explicitly clears it (e.g. a re-cluster
  // resets all cluster_ids to NULL, which we detect separately).
  const attemptedRef = useRef<Set<string>>(new Set());
  // Per-instance token so channel names never collide with another mount
  // (or a stale channel that hasn't finished removing yet).
  const instanceId = useMemo(() => crypto.randomUUID(), []);

  // Track whether the question already has clusters. We avoid firing placements
  // before the initial cluster_runs row exists.
  useEffect(() => {
    if (!questionId) {
      hasClustersRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const cs = await getLatestClusters(questionId);
      if (!cancelled) hasClustersRef.current = cs.length > 0;
    })();

    // Listen for the first cluster_runs row so subsequent placements can fire.
    const channel = supabase
      .channel(`live-placement-runs-${questionId}-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "cluster_runs", filter: `question_id=eq.${questionId}` },
        () => {
          hasClustersRef.current = true;
          // Fresh cluster scheme — everyone gets another chance. Clear the
          // attempted set so previously-uncategorized responses can be
          // re-placed against the new cluster set (which may include a
          // catch-all that didn't exist in the prior run).
          attemptedRef.current.clear();
          // After a fresh run, drain any responses that lingered as NULL.
          void drain();
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  // Drain: pop responses off the queue while we have capacity.
  const drain = async () => {
    while (queueRef.current.length > 0 && inFlightRef.current.size < MAX_CONCURRENT) {
      if (!hasClustersRef.current) return;
      const id = queueRef.current.shift();
      if (!id) continue;
      if (inFlightRef.current.has(id)) continue;
      inFlightRef.current.add(id);
      // Fire-and-forget. Errors logged, never thrown.
      requestPlacement(id)
        .then(() => {
          // Intentionally silent — surface only failures.
        })
        .catch((err) => {
          console.warn("[useLivePlacement] placement failed", id, err);
        })
        .finally(() => {
          inFlightRef.current.delete(id);
          // Mark this response as attempted so consider() won't re-enqueue
          // it after the placement-response UPDATE arrives (which would
          // otherwise immediately retry an uncategorized result).
          attemptedRef.current.add(id);
          // Recurse via setTimeout to break out of the synchronous loop.
          setTimeout(() => void drain(), 0);
        });
    }
  };

  // Enqueue a response if it qualifies and isn't already known to the queue.
  const consider = (r: Response) => {
    if (!isPlaceable(r)) return;
    if (inFlightRef.current.has(r.id)) return;
    if (queueRef.current.includes(r.id)) return;
    // Don't re-attempt a response that already had a placement round on this
    // session. If the edge function returned `uncategorized` (cluster_id
    // stays null), retrying immediately would just produce another
    // `uncategorized` and loop forever. The attempted set is cleared on a
    // fresh cluster_runs INSERT (everyone gets a new chance against the new
    // scheme) and on questionId change.
    if (attemptedRef.current.has(r.id)) return;
    queueRef.current.push(r.id);
    void drain();
  };

  // Subscribe to responses for the question.
  useEffect(() => {
    if (!questionId) return;

    // Backfill: on mount (or questionId change) read the current set of
    // visible responses with cluster_id IS NULL and enqueue any that are
    // placeable. Without this, responses that were submitted while no one
    // was watching (or that survived a page reload) sit in Außerhalb until
    // some unrelated event jogs the realtime stream. Both anon (StageView)
    // and authenticated (FacilitatorView) sessions can run this — RLS lets
    // anon SELECT responses for any visible event already.
    let backfillCancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("responses")
        .select("id, text, is_hidden, moderation_status, moderation_overridden, borderline_until, cluster_id")
        .eq("question_id", questionId)
        .is("cluster_id", null);
      if (backfillCancelled) return;
      if (error) {
        console.warn("[useLivePlacement] backfill query failed", error);
        return;
      }
      for (const row of data ?? []) {
        // The dedupe in `consider` (queue + inFlightRef) handles repeated
        // calls cleanly, so StrictMode double-mount is safe.
        consider(row as Response);
      }
    })();

    const channel = supabase
      .channel(`live-placement-responses-${questionId}-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "responses", filter: `question_id=eq.${questionId}` },
        (payload) => consider(payload.new as Response),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "responses", filter: `question_id=eq.${questionId}` },
        (payload) => consider(payload.new as Response),
      )
      .subscribe();

    return () => {
      backfillCancelled = true;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  // Reset queue + flight on question change so a stale queue from the previous
  // question never fires placements against the new one.
  useEffect(() => {
    queueRef.current = [];
    inFlightRef.current.clear();
    attemptedRef.current.clear();
  }, [questionId]);
}
